import { esc } from './html'

/**
 * The board drawn as a flat-earth chart. The hottest listing sits at the pole in the middle,
 * and each ring out holds listings with half the heat of the ring inside it. Past seven rings,
 * under 1/128 of the leader's heat, a listing ends up at the ice wall.
 */

export interface MapPoint {
  id: number
  name: string
  rank: number
  /** Current heat as a plain number of WIF. */
  heat: number
  /** All-time burned, as a plain number of WIF. */
  burned: number
}

const RINGS = 7
const LAND = 420 // radius of the inhabited disc
const WALL = 478 // outer edge of the ice wall
const GOLDEN = 137.50776405 // degrees, spreads ids evenly around the disc

/** How many rings out a listing sits, from 0 (the pole) to RINGS (the ice wall). */
export function ringsOut(heat: number, reference: number): number {
  if (heat <= 0 || reference <= 0) return RINGS
  return Math.min(RINGS, Math.max(0, Math.log2(reference / heat)))
}

function position(id: number, rings: number) {
  const r = (LAND * rings) / RINGS
  const a = ((id * GOLDEN + 90) % 360) * (Math.PI / 180)
  return { x: r * Math.cos(a), y: r * Math.sin(a), r }
}

/** Heat colour: ember at the pole, cooling into sea blue toward the wall. */
function heatColor(rings: number): string {
  const ember = Math.round(100 * (1 - rings / RINGS) ** 1.3)
  return `color-mix(in oklab, var(--ember) ${ember}%, var(--cold))`
}

/** A jagged inner edge for the ice wall, the same every render. */
function iceEdge(): string {
  const points: string[] = []
  const steps = 180
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2
    const jag = Math.sin(i * 7.3) * 5 + Math.sin(i * 2.1 + 1) * 4 + (i % 2 === 0 ? 6 : -2)
    const r = LAND + 14 + jag
    points.push(`${(r * Math.cos(a)).toFixed(1)},${(r * Math.sin(a)).toFixed(1)}`)
  }
  return `M${points.join('L')}Z`
}

const ICE_EDGE = iceEdge()

function chartFrame(): string {
  const rings = Array.from({ length: RINGS - 1 }, (_, i) => {
    const r = (LAND * (i + 1)) / RINGS
    return `<circle class="ring" r="${r.toFixed(1)}"/>`
  }).join('')
  const meridians = Array.from({ length: 24 }, (_, i) => {
    const a = (i / 24) * Math.PI * 2
    return `<line class="meridian" x1="0" y1="0" x2="${(LAND * Math.cos(a)).toFixed(1)}" y2="${(LAND * Math.sin(a)).toFixed(1)}"/>`
  }).join('')
  return `
    <defs>
      <path id="wall-arc" d="M ${-(WALL - 26)} 0 A ${WALL - 26} ${WALL - 26} 0 0 1 ${WALL - 26} 0"/>
    </defs>
    <circle class="ice" r="${WALL}"/>
    <path class="sea" d="${ICE_EDGE}"/>
    ${meridians}
    ${rings}
    <text class="wall-label"><textPath href="#wall-arc" startOffset="50%" text-anchor="middle">the ice wall</textPath></text>
    <g class="pole" aria-hidden="true">
      <path d="M0,-11 L2.6,-2.6 L11,0 L2.6,2.6 L0,11 L-2.6,2.6 L-11,0 L-2.6,-2.6Z"/>
    </g>`
}

export function renderMap(points: MapPoint[], selected: number | null): string {
  const reference = Math.max(0, ...points.map((p) => p.heat))
  const maxBurned = Math.max(1, ...points.map((p) => p.burned))

  // Draw coldest first so the hottest markers sit on top.
  const placed = points
    .map((p) => ({ p, rings: ringsOut(p.heat, reference) }))
    .sort((a, b) => b.rings - a.rings)

  const markers = placed
    .map(({ p, rings }) => {
      const { x, y } = position(p.id, rings)
      const size = 7 + 11 * Math.sqrt(p.burned / maxBurned)
      const labelled = p.rank <= 6 || p.id === selected
      const right = x >= 0
      return `
        <g class="marker${p.id === selected ? ' is-selected' : ''}" data-id="${p.id}" transform="translate(${x.toFixed(1)} ${y.toFixed(1)})"
           role="button" tabindex="0" aria-label="${esc(`${p.name}, number ${p.rank}`)}">
          <circle class="halo" r="${(size + 7).toFixed(1)}"/>
          <circle class="dot" r="${size.toFixed(1)}" style="fill:${heatColor(rings)}"/>
          ${labelled ? `<text class="marker-label" x="${right ? size + 7 : -(size + 7)}" y="5" text-anchor="${right ? 'start' : 'end'}">${esc(p.name)}</text>` : ''}
        </g>`
    })
    .join('')

  return `<svg class="chart-svg" viewBox="-500 -500 1000 1000" role="group" aria-label="Map of the board. The hottest projects sit in the middle.">
    ${chartFrame()}
    <g class="markers">${markers}</g>
  </svg>`
}
